import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Smile, TrendingUp, Baby, Waves, GraduationCap, ArrowRight, Heart, Users, User, Clock, Shell, Fish, Droplet, Trophy, CalendarDays } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import WhatToExpect from "@/components/site/WhatToExpect";
import CommunityBenefits from "@/components/site/CommunityBenefits";
import { SwimJourney } from "@/components/site/SwimJourney";
import TermDates from "@/components/site/TermDates";
import FinalCTA from "@/components/site/FinalCTA";
import WhereNext from "@/components/site/WhereNext";

const nextItems = [
  { sub: "Start with baby swimming", label: "Baby Swimming Lessons", to: "/classes/baby-swimming" },
  { sub: "How lessons are structured", label: "What to Expect", to: "/how-it-works" },
  { sub: "Check class availability", label: "Weekly Timetable", to: "/timetable" },
  { sub: "Speak to our team", label: "Contact Us", to: "/contact" },
];

const BOOK_URL = "https://portal.pivot1st.co.uk/book/2/class";

const moreWays = [
  {
    icon: <User size={26} />,
    title: "Adult Swimming Lessons",
    who: "Adults • 15+ · all levels",
    meta: "30 min lesson",
    desc: "Whether you're learning for the very first time, rebuilding lost confidence, refining your technique or training towards a goal, our adult lessons meet you exactly where you are — patient, private and pressure-free.",
    accent: "#0A2540",
  },
  {
    icon: <Heart size={26} />,
    title: "Happy Bumps",
    who: "Mums-to-be",
    meta: "45 min session",
    desc: "Gentle aqua natal sessions designed for pregnancy. Supportive, low-impact movement to help you relax, stay active and connect with other expectant parents before baby arrives.",
    accent: "#EC4899",
  },
  {
    icon: <Users size={26} />,
    title: "Little Tiddler Social",
    who: "Families with little ones",
    meta: "45 min social swim",
    desc: "A relaxed family social swim where little ones splash and play together. No lesson, no pressure — just a lovely, low-key way to build water confidence and meet other families.",
    accent: "#8B5CF6",
  },
  {
    icon: <Waves size={26} />,
    title: "Adult Social Swim",
    who: "Adults • all abilities",
    meta: "45 min social swim",
    desc: "Relaxed, informal pool time to practise at your own pace. All abilities welcome — a calm, friendly space to enjoy the water on your own terms.",
    accent: "#14B8A6",
  },
];

const constants = [
  { icon: <Sparkles size={22} />, text: "Confidence comes first", accent: "#FF7A1A" },
  { icon: <ShieldCheck size={22} />, text: "Safety is always prioritised", accent: "#0EA5E9" },
  { icon: <Smile size={22} />, text: "Learning is joyful and positive", accent: "#FF7A1A" },
  { icon: <TrendingUp size={22} />, text: "Progress is structured and visible", accent: "#0EA5E9" },
];

const classGroups = [
  {
    band: "Baby Swimming",
    ageBand: "8 weeks – 18 months",
    accent: "#4FA3C6",
    guide: "/classes/baby-swimming",
    guideLabel: "Read our full baby swimming guide",
    levels: [
      { icon: <Shell size={24} />, name: "Urchins", age: "8 – 12 weeks", desc: "Baby's very first, gentle introduction to the water." },
      { icon: <Fish size={24} />, name: "Small Fry", age: "3 – 9 months", desc: "Building trust and early water confidence through play." },
      { icon: <Baby size={24} />, name: "Guppies", age: "9 – 18 months", desc: "Growing curiosity and the first splashes of independence." },
    ],
  },
  {
    band: "Tiddler & Pre-School",
    ageBand: "18 months – 4½ years",
    accent: "#F59E0B",
    guide: "/classes/toddler-swimming",
    guideLabel: "Read our full tiddler & pre-school guide",
    levels: [
      { icon: <Baby size={24} />, name: "Tiny", age: "18 months – 2½ years", desc: "Growing curiosity and the first splashes of independence." },
      { icon: <Fish size={24} />, name: "Tetra", age: "2½ – 3½ years", desc: "Playful lessons building coordination and water confidence." },
      { icon: <Droplet size={24} />, name: "Tots", age: "3½ – 4½ years", desc: "Developing independence, breath control and early skills." },
      { icon: <Waves size={24} />, name: "Baby Sharks", age: "3½ – 4½ years · competency led", desc: "Getting ready to swim solo — the bridge to our Sharks pathway." },
    ],
  },
  {
    band: "Children's Swimming — Sharks",
    ageBand: "4½ years +",
    accent: "#1F7A8C",
    guide: "/classes/childrens-swimming",
    guideLabel: "Read our full children's swimming guide",
    levels: [
      { icon: <Trophy size={24} />, name: "Sharks 1", age: "4½ + · Beginner", desc: "Building water confidence and independent swimming." },
      { icon: <Trophy size={24} />, name: "Sharks 2", age: "4½ + · Swims 5m unaided", desc: "Developing stroke technique, distance and control." },
      { icon: <Trophy size={24} />, name: "Sharks 3", age: "6 + · Swims 10m unaided", desc: "Refining all four strokes, endurance and advanced skills." },
    ],
  },
];

export default function Classes() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="classes-page">
      <Seo
        title="Swimming Classes by Age — Babies to Adults | Happy Tiddlers"
        description="Explore all our swimming classes by age — from Urchins (8 weeks) through our Sharks pathway to adult lessons, in Barton-le-Clay, Bedfordshire."
        path="/classes"
      />
      <Navbar />

      {/* Header */}
      <section className="relative overflow-hidden pt-14 pb-10 md:pt-20 md:pb-14" data-testid="classes-header">
        <div className="blob absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#0EA5E9]/10" />
        <div className="container mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Our Classes</span>
              <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
                Swimming lessons designed around your child
              </h1>
              <p className="mt-6 text-lg text-[#334155] leading-relaxed">
                From babies taking their first splash to confident young swimmers building technique —
                and adults learning at their own pace — our lessons support every swimmer at every
                stage of their journey.
              </p>
              <div className="mt-7 flex flex-wrap gap-3" data-testid="classes-highlights">
                {[
                  { icon: <Waves size={15} />, text: "8 weeks – adults" },
                  { icon: <TrendingUp size={15} />, text: "9 progressive levels" },
                  { icon: <Sparkles size={15} />, text: "Warm 31–33°C water" },
                  { icon: <Users size={15} />, text: "Small classes" },
                ].map((h) => (
                  <span key={h.text} className="inline-flex items-center gap-2 bg-white border border-slate-100 shadow-sm text-[#0F172A] font-semibold text-sm px-4 py-2 rounded-full">
                    <span className="text-[#0EA5E9]">{h.icon}</span> {h.text}
                  </span>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="classes-header-ctas">
                <Link
                  to="/timetable"
                  className="inline-flex items-center gap-2 bg-[#0A2540] text-white font-bold px-6 py-3.5 rounded-full shadow-[0_8px_20px_-6px_rgba(10,37,64,0.5)] hover:-translate-y-0.5 transition-all duration-300"
                  data-testid="classes-timetable-cta"
                >
                  <CalendarDays size={18} /> View the timetable
                </Link>
                <a
                  href="https://portal.pivot1st.co.uk/book/2/class"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#0A2540] font-bold px-5 py-3.5 rounded-full border-2 border-[#0A2540] hover:bg-[#0A2540] hover:text-white transition-all duration-300"
                  data-testid="classes-book-cta"
                >
                  Book now <ArrowRight size={18} />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-[#0A2540]/20 -rotate-2 hover:rotate-0 transition-transform duration-500"
            >
              <img
                src={`${process.env.PUBLIC_URL}/classes-hero.jpg`}
                alt="A swim teacher and child smiling at each other during a lesson"
                className="w-full h-[320px] md:h-[460px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <SwimJourney />

      <TermDates />

      {/* What stays the same */}
      <section className="pb-20 md:pb-24" data-testid="classes-constants">
        <div className="container mx-auto px-6 md:px-12">
          <div className="relative overflow-hidden bg-[#0A2540] rounded-[2rem] p-8 md:p-14">
            <div className="blob absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#0EA5E9]/20" />
            <div className="blob absolute -bottom-28 -left-20 w-80 h-80 rounded-full bg-[#FF7A1A]/15" />
            <div className="relative">
              <span className="text-sm text-[#38BDF8] tracking-[0.2em] uppercase font-bold">The Happy Tiddlers promise</span>
              <h2 className="mt-4 font-heading text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight max-w-2xl">
                What stays the same at every level
              </h2>
              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {constants.map((c, i) => (
                  <motion.div
                    key={c.text}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="flex flex-col gap-4"
                    data-testid={`constant-${i}`}
                  >
                    <span className="grid place-items-center w-12 h-12 rounded-2xl text-white shrink-0" style={{ backgroundColor: c.accent }}>
                      {c.icon}
                    </span>
                    <p className="font-heading text-lg font-semibold text-white leading-snug">{c.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All class types by age */}
      <section className="pb-24 md:pb-32" data-testid="classes-pathways">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Classes by age</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
              Every class, from first splash to strong swimmer
            </h2>
            <p className="mt-5 text-lg text-[#334155]">
              A clear level for every age and stage — so your little one always knows what they're working towards.
            </p>
          </div>

          <div className="space-y-16">
            {classGroups.map((group, gi) => (
              <div key={group.band} data-testid={`class-group-${gi}`}>
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                  <div className="flex items-center gap-3">
                    <span className="w-1.5 h-10 rounded-full" style={{ backgroundColor: group.accent }} />
                    <div>
                      <h3 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">{group.band}</h3>
                      <span className="text-sm font-semibold text-[#64748B]">{group.ageBand}</span>
                    </div>
                  </div>
                  <Link
                    to={group.guide}
                    className="inline-flex items-center gap-1.5 text-sm font-bold hover:gap-3 transition-all"
                    style={{ color: group.accent }}
                    data-testid={`class-group-guide-${gi}`}
                  >
                    {group.guideLabel} <ArrowRight size={15} />
                  </Link>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.levels.map((l, i) => (
                    <motion.div
                      key={l.name}
                      initial={{ opacity: 0, y: 22 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.45, delay: i * 0.07 }}
                      className="group bg-white rounded-[1.75rem] p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
                      data-testid={`class-card-${gi}-${i}`}
                    >
                      <span
                        className="grid place-items-center w-12 h-12 rounded-2xl text-white shadow-md mb-4 group-hover:scale-110 transition-transform duration-300"
                        style={{ backgroundColor: group.accent }}
                      >
                        {l.icon}
                      </span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-heading text-lg font-bold text-[#0F172A]">{l.name}</h4>
                      </div>
                      <span
                        className="inline-block mt-1.5 text-xs font-bold px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: `${group.accent}1A`, color: group.accent }}
                      >
                        {l.age}
                      </span>
                      <p className="mt-3 text-[#64748B] leading-relaxed">{l.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-[#0A2540] rounded-[2rem] p-8 md:p-12 text-center relative overflow-hidden">
            <div className="blob absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#0EA5E9]/20" />
            <p className="relative text-lg md:text-xl text-[#CBD5E1] max-w-3xl mx-auto leading-relaxed">
              Not sure which level fits? Our team will help place your child at exactly the right stage —
              and move them up when they're ready.
            </p>
            <div className="relative mt-8 flex flex-wrap gap-4 justify-center">
              <a
                href="https://portal.pivot1st.co.uk/book/2/class"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-1 transition-all duration-300"
                data-testid="classes-find-class-cta"
              >
                Find the right class for your child <ArrowRight size={18} />
              </a>
              <Link
                to="/timetable"
                className="inline-flex items-center gap-2 bg-white/10 text-white font-bold px-8 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300"
                data-testid="classes-timetable-cta-bottom"
              >
                See the timetable
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* More ways to swim */}
      <section className="pb-24 md:pb-32" data-testid="classes-more-ways">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">More ways to swim</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
              Something for every stage of life
            </h2>
            <p className="mt-5 text-lg text-[#334155]">
              From bump to grown-up, and relaxed social swims for the whole family — there's a place
              in the water for everyone at Happy Tiddlers.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
            {moreWays.map((m, i) => (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 flex flex-col"
                data-testid={`more-way-card-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl text-white shrink-0" style={{ backgroundColor: m.accent }}>
                    {m.icon}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-100 text-[#334155]">
                    <Clock size={13} /> {m.meta}
                  </span>
                </div>
                <h3 className="mt-6 font-heading text-xl font-semibold tracking-tight text-[#0F172A]">{m.title}</h3>
                <span className="text-sm font-semibold text-[#64748B] mb-3">{m.who}</span>
                <p className="text-[#334155] leading-relaxed flex-1">{m.desc}</p>
                <a
                  href={BOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-6 font-bold text-[#0EA5E9] hover:gap-3 transition-all"
                  data-testid={`more-way-link-${i}`}
                >
                  Book or enquire <ArrowRight size={16} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="pb-20 md:pb-24" data-testid="classes-pricing-teaser">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white rounded-[2rem] p-8 md:p-10 border border-slate-100 shadow-sm">
            <div>
              <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Pricing</span>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A] leading-tight">
                Premium lessons, fairly priced
              </h2>
              <p className="mt-2 text-[#334155] max-w-xl">
                Prices are set by age group and session length, and kept as competitive as we can —
                with no hidden fees. See the full breakdown on our pricing page.
              </p>
            </div>
            <Link
              to="/pricing"
              className="shrink-0 inline-flex items-center gap-2 bg-[#0A2540] text-white font-bold px-7 py-4 rounded-full hover:-translate-y-0.5 transition-all duration-300"
              data-testid="classes-pricing-link"
            >
              View pricing &amp; packages <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <CommunityBenefits />
      <WhatToExpect />
      <FinalCTA />
      <WhereNext items={nextItems} />
      <Footer />
    </div>
  );
}
